import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  uploadProjectFieldFile,
  uploadProjectFieldFileAppend,
  clearProjectFieldFile,
} from "./plugins/projectFieldFilesApi";
import {
  downloadProjectFieldFileBlob,
  isProjectFieldFileContentUrl,
  openAuthenticatedProjectFieldFile,
  triggerBrowserDownload,
} from "./projectFieldFileAuth";
import {
  fileDisplayLabelFromStoredValue,
  fileUrlFromStoredValue,
} from "./plugins/CommercialFieldFileDisplay";
import { isPluginEnabled, PLUGIN_IDS } from "./plugins/registry";

export function shouldOfferProjectFileEditing(pathPrefix) {
  if (!pathPrefix || pathPrefix.length < 2) return false;
  const last = String(pathPrefix[pathPrefix.length - 1] || "").toLowerCase();
  const prev = pathPrefix[pathPrefix.length - 2];
  if (last === "site_walk_file" && typeof prev === "number") return true;
  if (pathPrefix.some((p) => typeof p === "number")) return false;
  return true;
}

export function fieldPathLooksFileLike(pathPrefix, stringValue) {
  if (!pathPrefix || pathPrefix.length < 2) return false;
  const leaf = String(pathPrefix[pathPrefix.length - 1] || "").toLowerCase();
  if (leaf === "site_walk_files" && pathPrefix.length === 2) return true;
  if (leaf === "incident_photos" && pathPrefix.length === 2) return true;
  if (leaf === "damage_report" && pathPrefix.length === 2) return true;
  if (!shouldOfferProjectFileEditing(pathPrefix)) return false;
  if (
    /(^|_)files?$|_file$|^file$|photos?$|attachment|pdf|document|upload|walk|permit|fiber|drawing|plan|sheet/i.test(
      leaf,
    )
  ) {
    return true;
  }
  const s = String(stringValue || "").trim();
  if (/^https?:\/\//i.test(s)) return true;
  return false;
}

/** DELETE uses [group, site_walk_files, rowIndex] for table rows (not the leaf path). */
function fieldPathForFileRemoval(fieldPath) {
  if (
    fieldPath.length >= 4 &&
    typeof fieldPath[fieldPath.length - 2] === "number" &&
    String(fieldPath[fieldPath.length - 1] || "").toLowerCase() === "site_walk_file"
  ) {
    return [...fieldPath.slice(0, -2), fieldPath[fieldPath.length - 2]];
  }
  return fieldPath;
}

function fileDropZoneStyle(dragOver) {
  return dragOver
    ? {
        borderColor: "#2563eb",
        background: "#eff6ff",
        padding: "8px 10px",
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: "dashed",
      }
    : {
        padding: "8px 10px",
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: "dashed",
        borderColor: "transparent",
      };
}

export function fileMatchesAccept(file, accept) {
  if (!file || !accept) return true;
  const name = String(file.name || "").toLowerCase();
  const mime = String(file.type || "").toLowerCase();
  return String(accept)
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
    .some((part) => {
      if (part.startsWith(".")) return name.endsWith(part);
      if (part.includes("/")) return mime === part;
      return true;
    });
}

function useFileDropZone({ disabled, onFiles, accept }) {
  const [dragOver, setDragOver] = useState(false);

  const onDragEnter = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setDragOver(true);
    },
    [disabled]
  );

  const onDragOver = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setDragOver(true);
    },
    [disabled]
  );

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (disabled) return;
      const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
      if (!files.length) return;
      const accepted = accept ? files.filter((file) => fileMatchesAccept(file, accept)) : files;
      if (accepted.length) onFiles(accepted);
    },
    [accept, disabled, onFiles]
  );

  return { dragOver, onDragEnter, onDragOver, onDragLeave, onDrop };
}

export function ProjectSiteWalkFilesAppendControls({ projectId, fieldPath, getAuthHeaders, onUpdated, accept }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const pickFiles = () => inputRef.current?.click();

  const uploadFiles = useCallback(
    async (files) => {
      const list = Array.isArray(files) ? files : [];
      if (!list.length) return;
      setBusy(true);
      setError("");
      try {
        let lastResult;
        for (const file of list) {
          if (!fileMatchesAccept(file, accept)) continue;
          lastResult = await uploadProjectFieldFileAppend(projectId, fieldPath, file, getAuthHeaders);
        }
        onUpdated?.(lastResult ? { ...lastResult, fieldPath } : undefined);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || "Upload failed");
      } finally {
        setBusy(false);
      }
    },
    [accept, fieldPath, getAuthHeaders, onUpdated, projectId]
  );

  const onChange = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    await uploadFiles(files);
  };

  const dropZone = useFileDropZone({
    disabled: busy,
    accept,
    onFiles: uploadFiles,
  });

  return (
    <div
      className={`project-field-file-controls project-site-walk-append${
        dropZone.dragOver ? " project-field-file-controls--drag-over" : ""
      }`}
      style={fileDropZoneStyle(dropZone.dragOver)}
      onDragEnter={dropZone.onDragEnter}
      onDragOver={dropZone.onDragOver}
      onDragLeave={dropZone.onDragLeave}
      onDrop={dropZone.onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        className="project-field-file-input"
        multiple
        accept={accept}
        onChange={onChange}
        disabled={busy}
      />
      <div className="project-field-file-actions">
        <button type="button" className="project-field-file-btn" onClick={pickFiles} disabled={busy}>
          {busy ? "Uploading…" : "Add file(s)"}
        </button>
        <span className="project-field-file-drop-hint" style={{ fontSize: 12, color: "#6b7280" }}>
          or drag files here
        </span>
      </div>
      {error ? (
        <p className="project-field-file-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default function ProjectFieldFileControls({
  projectId,
  fieldPath,
  value,
  getAuthHeaders,
  onUpdated,
  accept,
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const [extractNote, setExtractNote] = useState("");
  const fieldKey = (() => {
    const path = Array.isArray(fieldPath) ? fieldPath : [];
    const leaf = path.length ? String(path[path.length - 1] || "") : "";
    // Multifile rows use [...fieldKey, index, "site_walk_file"] — label from the real field key.
    if (
      leaf.toLowerCase() === "site_walk_file" &&
      path.length >= 3 &&
      typeof path[path.length - 2] === "number"
    ) {
      const multifileKey = path[path.length - 3];
      if (multifileKey != null && String(multifileKey).trim() !== "") {
        return String(multifileKey);
      }
    }
    return leaf;
  })();
  const [displayUrl, setDisplayUrl] = useState(() => fileUrlFromStoredValue(value));
  const [displayLabel, setDisplayLabel] = useState(() =>
    fileDisplayLabelFromStoredValue(value, { fieldKey, accept })
  );

  useEffect(() => {
    setDisplayUrl(fileUrlFromStoredValue(value));
    setDisplayLabel(fileDisplayLabelFromStoredValue(value, { fieldKey, accept }));
  }, [value, fieldKey, accept]);

  const hasHttp = typeof displayUrl === "string" && /^https?:\/\//i.test(displayUrl);
  const isOurFile = hasHttp && isProjectFieldFileContentUrl(displayUrl);

  const pickFile = () => inputRef.current?.click();

  const uploadFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!fileMatchesAccept(file, accept)) {
        setError("That file type is not allowed here.");
        return;
      }
      setBusy(true);
      setError("");
      setExtractNote("");
      try {
        const result = await uploadProjectFieldFile(projectId, fieldPath, file, getAuthHeaders);
        if (result?.url) {
          setDisplayUrl(String(result.url));
          const stored = {
            url: result.url,
            filename: result.filename || file.name,
          };
          setDisplayLabel(
            fileDisplayLabelFromStoredValue(stored, { fieldKey, accept }) || file.name || "Attached file"
          );
        }
        const ppm = result?.permitPlanMaterials;
        const extractionOn = isPluginEnabled(PLUGIN_IDS.PERMIT_PLAN_MATERIAL_EXTRACTION);
        if (fieldKey === "permit_plan" && extractionOn && ppm?.applied) {
          const count = ppm.rowCount ?? ppm.materials?.length ?? 0;
          setExtractNote(
            `${count} material line${count === 1 ? "" : "s"} imported from the plan — verify items and quantities below.`
          );
        } else if (fieldKey === "permit_plan" && extractionOn && ppm && !ppm.applied) {
          const err = ppm.error || "";
          setExtractNote(
            err === "no_text" || err === "no_material_table_text"
              ? "The material table on this plan is not readable text (likely CAD outlines) — enter materials manually."
              : err === "ocr_no_rows"
                ? "OCR could not read material totals on page 1 — enter materials manually."
                : "No material totals were detected on this plan — enter materials manually."
          );
        }
        onUpdated?.(result ? { ...result, fieldPath } : undefined);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || "Upload failed");
      } finally {
        setBusy(false);
      }
    },
    [accept, fieldKey, fieldPath, getAuthHeaders, onUpdated, projectId]
  );

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await uploadFile(file);
  };

  const dropZone = useFileDropZone({
    disabled: busy,
    accept,
    onFiles: (files) => uploadFile(files[0]),
  });

  const onRemove = async () => {
    if (!window.confirm("Remove this file from the project?")) return;
    setBusy(true);
    setError("");
    try {
      const result = await clearProjectFieldFile(projectId, fieldPathForFileRemoval(fieldPath), getAuthHeaders);
      setDisplayUrl("");
      setDisplayLabel("");
      onUpdated?.(result ? { ...result, fieldPath } : undefined);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  const onView = async () => {
    if (!hasHttp) return;
    const url = displayUrl;
    if (isOurFile) {
      setError("");
      setOpening(true);
      try {
        await openAuthenticatedProjectFieldFile(url, {
          onError: (msg) => setError(msg || "Could not open file"),
          filename: displayLabel,
          accept,
        });
      } finally {
        setOpening(false);
      }
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onDownload = async () => {
    if (!hasHttp || !isOurFile) return;
    const url = displayUrl;
    setError("");
    try {
      const blob = await downloadProjectFieldFileBlob(url);
      triggerBrowserDownload(blob, displayLabel || "download");
    } catch (err) {
      setError(err?.message || "Download failed");
    }
  };

  return (
    <div
      className={`project-field-file-controls${
        dropZone.dragOver ? " project-field-file-controls--drag-over" : ""
      }`}
      style={fileDropZoneStyle(dropZone.dragOver)}
      onDragEnter={dropZone.onDragEnter}
      onDragOver={dropZone.onDragOver}
      onDragLeave={dropZone.onDragLeave}
      onDrop={dropZone.onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        className="project-field-file-input"
        accept={accept}
        onChange={onFileChange}
        disabled={busy}
      />
      {hasHttp ? (
        <p className="commercial-file-attached">
          <button
            type="button"
            className="commercial-file-view-link"
            onClick={onView}
            disabled={busy || opening}
          >
            {opening ? "Opening…" : displayLabel || (isOurFile ? "View uploaded file" : "Open link")}
          </button>
        </p>
      ) : null}
      <div className="project-field-file-actions">
        <button type="button" className="project-field-file-btn" onClick={pickFile} disabled={busy}>
          {busy ? "Working…" : hasHttp ? "Replace file" : "Upload file"}
        </button>
        {hasHttp && isOurFile ? (
          <button type="button" className="project-field-file-btn" onClick={onDownload} disabled={busy}>
            Download
          </button>
        ) : null}
        {hasHttp ? (
          <button type="button" className="project-field-file-btn danger" onClick={onRemove} disabled={busy}>
            Remove
          </button>
        ) : null}
        <span className="project-field-file-drop-hint" style={{ fontSize: 12, color: "#6b7280" }}>
          or drag a file here
        </span>
      </div>
      {error ? (
        <p className="project-field-file-error" role="alert">
          {error}
        </p>
      ) : null}
      {extractNote ? (
        <p
          className="project-field-file-extract-note"
          role="status"
          style={{
            margin: "8px 0 0",
            fontSize: 12,
            lineHeight: 1.45,
            color: "#92400e",
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 8,
            padding: "8px 10px",
          }}
        >
          {extractNote}
        </p>
      ) : null}
    </div>
  );
}
